import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import { MoreVertMenu } from './MoreVertMenu';
import Tags from './Tags';
import { IFigureDto } from '../models/IFigureDto';
import Breadcrumbs from './Breadcrumbs';

import ItemComment from './DetailComment';
import { StatusChip } from './StatusChip';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChangeHistory from './ChangeHistory';
import DataDetails from './DataDetails';
import { formatNorwegianDate, sanitizeHtml, formatDocNumber } from '../utils/formatters';
import Series from './Series';
import { getCollectionColors } from '../utils/collectionColors';
import { useConfig } from '../config/env';
import { useFigureDetailState } from '../hooks/useFigureDetailState';

interface FigureDetailProps {
  figure: IFigureDto;
  collectionId: number;
  onForceGridView?: () => void;
  onAvatarClick?: (collectionId: number) => void;
  collections?: Array<{ id: number; name: string; color?: string }>;
}

const FigureDetail: React.FC<FigureDetailProps> = ({ figure: initialFigure, collectionId, onForceGridView: _onForceGridView, onAvatarClick, collections = [] }) => {
  const cfg = useConfig();
  const commentRef = useRef<HTMLInputElement>(null);
  const {
    figure,
    setFigure,
    isFav,
    handleFavoriteClick,
    versionHistory,
    isZoomed,
    setIsZoomed,
    forceExpanded,
    setForceExpanded,
    selectedCollectionTab,
    setSelectedCollectionTab,
    newCollectionName,
    setNewCollectionName,
    collectionMenuAnchor,
    availableCollections,
    collectionFieldRef,
    popoverContentRef,
    handleAddButtonClick,
    handleCollectionMenuClose,
    handleCollectionSelect,
    handleAddToCollection,
    safeStatus,
  } = useFigureDetailState({ initialFigure, collectionId, collections, onAvatarClick });

  const handleEditClick = () => {
    setForceExpanded(true); // Reset any user-initiated closes
    setTimeout(() => commentRef.current?.focus(), 100);
  };

  const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
  const collectionName = collectionId === favoriteId ? 'Favoritter' : collections.find((c) => c.id === collectionId)?.name || '';
  const defaultId = Number(cfg?.defaultCollectionId ?? 0);
  const defaultCollectionName = collections.find((c) => c.id === defaultId)?.name || 'Byggforskserien';
  const isDefaultCollection = collectionId === defaultId;

  const anchorId = (() => {
    const raw = figure.figureNumber ?? '';
    const digits = (raw.match(/\d+/) || [''])[0];
    return digits ? `i${parseInt(digits, 10)}` : undefined;
  })();
  const latestVersion = versionHistory.length > 0 ? versionHistory[0] : null;
  const documentLink = latestVersion
    ? `https://byggforsk.no/dokument/${latestVersion.docId}?version=${encodeURIComponent(latestVersion.guideVersionPublicId)}${
        anchorId ? `#${anchorId}` : ''
      }`
    : undefined;

  const breadcrumbItems = [
    {
      label: defaultCollectionName,
      onClick: onAvatarClick && defaultId > 0 ? () => onAvatarClick(defaultId) : undefined,
    },
    ...(isDefaultCollection
      ? []
      : [
          {
            label: collectionName,
            onClick: onAvatarClick ? () => onAvatarClick(collectionId) : undefined,
          },
        ]),
    {
      label:
        `${figure.docNumber ? formatDocNumber(figure.docNumber) : ''}${figure.docNumber && figure.title ? ' ' : ''}${figure.title || ''}`.trim() ||
        'Untitled',
      href: documentLink,
    },
    ...(figure.figureNumber ? [{ label: `Figur ${figure.figureNumber}`, href: documentLink }] : []),
  ];

  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}>
      {onAvatarClick && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => onAvatarClick(collectionId)}
          sx={{
            mb: 2,
            textTransform: 'none',
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}>
          Tilbake til samling {collectionName}
        </Button>
      )}
      <Breadcrumbs items={breadcrumbItems} />

      <Paper
        elevation={2}
        sx={{
          p: 3,
          flex: 1,
          mt: 2,
          bgcolor: 'background.paper',
          width: '785px',
        }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxHeight: '500px',
            overflow: 'hidden',
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <img
            src={`/image?docNumber=${figure.docNumber}&versionId=${figure.versionId}&fileName=${figure.zoomFile}`}
            alt={figure.imgName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
          <IconButton
            onClick={() => setIsZoomed(true)}
            sx={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}>
            <ZoomInIcon />
          </IconButton>
        </Box>

        <Box sx={{ bgcolor: '#efefef', p: 2, borderRadius: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              mb: 1,
              gap: 0.5,
            }}>
            <IconButton
              size="small"
              sx={{
                mr: 0.5,
                padding: '2px',
                alignSelf: 'flex-start',
              }}>
              {/* <NoteAddIcon fontSize="small" /> */}
              <Series seriesId={figure.seriesId} size={32} />
            </IconButton>

            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
              <Typography
                variant="body1"
                sx={{
                  minWidth: 'auto',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                }}>
                {figure.docNumber && formatDocNumber(figure.docNumber)}
                {figure.docNumber && figure.title && ' '}
                {figure.title}
                {!figure.docNumber && !figure.title && figure.imgName}
              </Typography>
              {figure.figureNumber && (
                <Typography
                  variant="body1"
                  sx={{
                    minWidth: 'auto',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                  }}>
                  Fig. {figure.figureNumber}
                </Typography>
              )}
            </Box>

            <Box sx={{ flexGrow: 1 }} />
            <StatusChip
              status={safeStatus}
              lastUpdated={formatNorwegianDate(figure.publishedDate?.toString())}
              published={formatNorwegianDate(figure.createdDate?.toString())}
              description="Some text here please - from where?"
            />

            <IconButton
              size="small"
              onClick={handleFavoriteClick}
              sx={{
                color: isFav ? 'error.main' : 'action.active',
                padding: '2px',
              }}>
              {isFav ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            </IconButton>

            <MoreVertMenu onEdit={handleEditClick} onHelp={() => {}} customerId={1} figureId={figure.imageId} collectionId={collectionId} />
          </Box>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              mb: 0.5,
              fontSize: '0.75rem',
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(figure.captionHtml ?? '', 600),
            }}
          />
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              mb: 0.5,
              fontSize: '1.0rem',
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(figure.context ?? ''),
            }}
          />
          <DataDetails
            docNumber={figure.docNumber || ''}
            guideVersionPublicId={figure.guideVersionPublicId ?? ''}
            publishedDate={figure.publishedDate?.toString()}
            imgName={figure.imgName}
            large
          />
          <ChangeHistory
            versions={versionHistory}
            anchorId={(function () {
              const raw = figure.figureNumber ?? '';
              const digits = (raw.match(/\d+/) || [''])[0];
              return digits ? `i${parseInt(digits, 10)}` : undefined;
            })()}
          />
        </Box>

        {/* {figure.collectionMemberships && Object.keys(figure.collectionMemberships).length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }} noWrap>
                {Object.values(figure.collectionMemberships).join(', ')}
              </Typography>
            </Box>
          )} */}

        {(() => {
          const defaultId = Number(cfg?.defaultCollectionId ?? 0);
          const defaultCollectionIdStr = defaultId > 0 ? String(defaultId) : null;

          // Create combined collection memberships that always includes the default collection
          const combinedMemberships: Record<string, string> = { ...figure.collectionMemberships };
          if (defaultCollectionIdStr && !combinedMemberships[defaultCollectionIdStr]) {
            // Find default collection name from collections prop, or use fallback
            const defaultCollection = collections.find((c) => c.id === defaultId);
            combinedMemberships[defaultCollectionIdStr] = defaultCollection?.name || 'Byggforskserien';
          }

          // Show tabs if there are any collections (including the default)
          if (Object.keys(combinedMemberships).length === 0) return null;

          // Sort entries: default collection first, then others
          const sortedEntries = Object.entries(combinedMemberships).sort(([idA], [idB]) => {
            const isDefaultA = defaultId > 0 && Number(idA) === defaultId;
            const isDefaultB = defaultId > 0 && Number(idB) === defaultId;
            if (isDefaultA && !isDefaultB) return -1;
            if (!isDefaultA && isDefaultB) return 1;
            return 0;
          });

          // Only render Tabs if we have a valid selectedCollectionTab or at least one entry
          if (sortedEntries.length === 0) return null;

          // Ensure selectedCollectionTab is valid, default to first entry if not
          const validTabValue =
            selectedCollectionTab && sortedEntries.some(([id]) => id === selectedCollectionTab)
              ? selectedCollectionTab
              : sortedEntries[0]?.[0] || false;

          return (
            <Box sx={{ mt: '15px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0 }}>
              {sortedEntries.map(([id, name], index) => {
                const collectionIdNum = Number(id);
                const collection = collections.find((c) => c.id === collectionIdNum);
                const isDefault = defaultId > 0 && collectionIdNum === defaultId;
                const collectionColor = collection
                  ? getCollectionColors({
                      id: String(collection.id),
                      name: collection.name,
                      isDefault: isDefault,
                      color: collection.color,
                    }).bg
                  : getCollectionColors({ id, name, isDefault }).bg;
                const isSelected = id === validTabValue;
                return (
                  <Button
                    key={id}
                    onClick={() => {
                      setSelectedCollectionTab(id);
                    }}
                    sx={{
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: '4px 4px 0 0',
                      marginLeft: index === 0 ? 0 : '-1px',
                      padding: '0px 12px',
                      height: '32px',
                      minHeight: '32px',
                      lineHeight: '32px',
                      fontWeight: isSelected ? 700 : 600,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      minWidth: 'unset',
                      bgcolor: isSelected ? '#efefef' : 'white',
                      color: 'text.primary',
                      marginBottom: isSelected ? '-1px' : 0,
                      borderBottom: collectionColor ? `3px solid ${collectionColor} !important` : isSelected ? '1px solid #efefef' : 'none',
                      '&:hover': {
                        bgcolor: isSelected ? '#efefef' : 'grey.200',
                      },
                    }}>
                    {name}
                  </Button>
                );
              })}
              {/* Add to collection button */}
              <Box ref={collectionFieldRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Button
                  onClick={handleAddButtonClick}
                  sx={{
                    minWidth: '32px',
                    padding: '2px 12px',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderBottom: 'none',
                    borderRadius: '4px 4px 0 0',
                    marginLeft: '-1px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    bgcolor: 'white',
                    color: 'text.primary',
                    height: '32px',
                    minHeight: '32px',
                    lineHeight: '32px',
                    textTransform: 'none',
                    '&:hover': {
                      bgcolor: 'grey.200',
                    },
                  }}>
                  +
                </Button>
              </Box>
              <Menu
                open={Boolean(collectionMenuAnchor)}
                anchorEl={collectionMenuAnchor}
                onClose={handleCollectionMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                MenuListProps={{
                  sx: {
                    py: 0,
                    maxHeight: 300,
                    width: '20ch',
                    overflow: 'hidden',
                  },
                }}
                PaperProps={{
                  sx: {
                    maxHeight: 300,
                    width: '20ch',
                    mt: 0.5,
                    overflow: 'hidden',
                  },
                }}>
                <Box ref={popoverContentRef}>
                  <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                    <TextField
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleAddToCollection();
                        }
                      }}
                      variant="standard"
                      fullWidth
                      autoFocus
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '0.875rem',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                        },
                      }}
                      inputProps={{ 'aria-label': 'Lag ny samling', placeholder: 'Lag ny samling' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleAddToCollection();
                              }}
                              aria-label="Legg til i samling"
                              disabled={!newCollectionName.trim()}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  <List
                    sx={{
                      py: 0,
                      maxHeight: 250,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}>
                    {availableCollections
                      .filter((collection) => {
                        const defaultIdLocal = Number(cfg?.defaultCollectionId ?? 0);
                        const isDefaultById = defaultIdLocal > 0 && collection.id === defaultIdLocal;
                        const isDefaultFlag = (collection as { isDefault?: boolean }).isDefault === true;
                        return !(isDefaultById || isDefaultFlag);
                      })
                      .filter((collection) => {
                        // Filter out collections the figure is already in
                        // collectionMemberships uses numeric keys
                        const isAlreadyMember = figure.collectionMemberships?.[collection.id] !== undefined;
                        return !isAlreadyMember;
                      })
                      .map((collection) => {
                        const collectionColor = getCollectionColors({
                          id: String(collection.id),
                          name: collection.name,
                          color: collection.color,
                        });
                        return (
                          <ListItem key={collection.id} disablePadding>
                            <ListItemButton
                              onClick={() => {
                                void handleCollectionSelect(collection.id);
                              }}
                              sx={{
                                py: 1,
                                px: 1.5,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <FolderIcon
                                  sx={{
                                    color: collectionColor.bg,
                                    fontSize: '1.2rem',
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={collection.name}
                                secondary={collection.comment}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                }}
                                secondaryTypographyProps={{
                                  fontSize: '0.75rem',
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    {availableCollections.filter((collection) => {
                      const defaultIdLocal = Number(cfg?.defaultCollectionId ?? 0);
                      const isDefaultById = defaultIdLocal > 0 && collection.id === defaultIdLocal;
                      const isDefaultFlag = (collection as { isDefault?: boolean }).isDefault === true;
                      const isAlreadyMember = figure.collectionMemberships?.[collection.id] !== undefined;
                      return !(isDefaultById || isDefaultFlag) && !isAlreadyMember;
                    }).length === 0 && (
                      <ListItem disablePadding>
                        <ListItemButton disabled>
                          <ListItemText primary="Ingen samlinger tilgjengelig" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Menu>
            </Box>
          );
        })()}

        <Box sx={{ bgcolor: '#efefef', p: 2, borderRadius: 1 }}>
          <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
            <Box
              component="fieldset"
              sx={{
                border: '1px solid #d1d5db',
                borderRadius: 1.5,
                bgcolor: '#efefef',
                pt: 0.75,
                px: 1,
                pb: 1,
                width: '100%',
                maxWidth: 750,
                m: 0,
              }}>
              <Typography
                component="legend"
                sx={{
                  px: 0.75,
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  letterSpacing: 0.3,
                  color: 'text.secondary',
                }}>
                Legg til tags
              </Typography>
              {(() => {
                const activeCollectionId =
                  selectedCollectionTab && typeof selectedCollectionTab === 'string' ? Number(selectedCollectionTab) : collectionId;
                const defaultId = Number(cfg?.defaultCollectionId ?? 0);
                const isDefaultCollectionTab = activeCollectionId === defaultId;

                return (
                  <Tags
                    imageData={figure}
                    showRemovableTags={!isDefaultCollectionTab}
                    collectionId={collectionId}
                    filterCollectionId={activeCollectionId}
                    collections={collections}
                    hideAddTag={isDefaultCollectionTab}
                    showAllTags={isDefaultCollectionTab}
                    onUpdate={(updated) => {
                      // Merge only tag-related fields to avoid clobbering other user state
                      setFigure((prev) => ({
                        ...prev,
                        customerMetadata: updated.customerMetadata,
                        metadata: updated.metadata ?? prev.metadata,
                      }));
                    }}
                  />
                );
              })()}
            </Box>
          </Box>
          {(() => {
            const activeCollectionId = selectedCollectionTab ? Number(selectedCollectionTab) : collectionId;
            const defaultId = Number(cfg?.defaultCollectionId ?? 0);
            const isDefaultCollectionTab = activeCollectionId === defaultId;

            return (
              <ItemComment
                expanded={forceExpanded}
                onExpandedChange={(expanded: boolean) => setForceExpanded(expanded)}
                imageData={figure}
                collectionId={activeCollectionId}
                collections={collections}
                readOnly={isDefaultCollectionTab}
                onUpdate={(updatedData) => {
                  setFigure({
                    ...figure,
                    customerComment: updatedData.customerComment,
                  });
                }}
              />
            );
          })()}
        </Box>
      </Paper>

      <Dialog
        open={isZoomed}
        onClose={() => setIsZoomed(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            margin: 2,
            maxWidth: '800px',
            width: '100%',
          },
        }}>
        <Box
          sx={{
            position: 'relative',
            backgroundColor: 'white',
            p: 4,
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '80vh',
            minHeight: '200px',
            '& img': {
              margin: '32px 0',
            },
          }}>
          <IconButton
            onClick={() => setIsZoomed(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
              zIndex: 1,
            }}>
            <CloseIcon />
          </IconButton>

          <img
            src={`/image?docNumber=${figure.docNumber}&versionId=${figure.versionId}&fileName=${figure.zoomFile}`}
            alt={figure.imgName}
            style={{
              maxWidth: '100%',
              maxHeight: 'calc(80vh - 100px)',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />

          <Button
            variant="contained"
            onClick={() => setIsZoomed(false)}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              backgroundColor: 'white',
              color: 'black',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              boxShadow: 1,
              zIndex: 1,
            }}>
            Lukk
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default FigureDetail;
